import { useState } from "react";
import {
  Clock, Pencil, Plus, Settings2, Trash2, Zap,
} from "lucide-react";
import { AppShell } from "@/shared/layouts/AppShell.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Switch } from "@/shared/ui/switch.jsx";
import { LoadingState, EmptyState } from "@/shared/ui/spinner.jsx";
import { cn } from "@/shared/lib/cn.js";
import { StepEditorDialog } from "./components/StepEditorDialog.jsx";
import {
  DeleteJourneyDialog, EditJourneyDialog, NewJourneyDialog, ScheduleDialog,
} from "./components/JourneyDialogs.jsx";
import { useAutomations, useDeleteStep, useUpdateAutomation } from "./use-automations.js";
import { formatSchedule, stepTypeMeta } from "./automation-constants.js";

/**
 * Construtor de jornadas de mensagens.
 *
 * A tela antiga tinha 923 linhas num arquivo só: os quatro diálogos, o
 * editor de etapa e a lista de jornadas todos misturados, com o estado
 * inteiro em `useState` sincronizado com o `localStorage`. Aqui a página
 * cuida do layout, os diálogos moram em `components/` e os dados vêm do
 * servidor.
 */
export function AutomationPage() {
  const { data: jornadas = [], isLoading } = useAutomations();
  const atualizar = useUpdateAutomation();
  const removerEtapa = useDeleteStep();

  const [selecionadaId, setSelecionadaId] = useState(null);
  const [dialogos, setDialogos] = useState({ nova: false, editar: false, janela: false, excluir: false });
  const [etapaEmEdicao, setEtapaEmEdicao] = useState({ aberta: false, etapa: null });

  const ativa = jornadas.find((j) => j.id === selecionadaId) ?? jornadas[0] ?? null;

  const abrir = (qual) => setDialogos((atual) => ({ ...atual, [qual]: true }));
  const fechar = (qual) => setDialogos((atual) => ({ ...atual, [qual]: false }));

  if (isLoading) {
    return (
      <AppShell title="Automação de Jornada" subtitle="Fluxos de mensagens por procedimento">
        <LoadingState message="Carregando jornadas..." />
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Automação de Jornada"
      subtitle="Construa e edite fluxos de mensagens para cada procedimento"
      actions={
        <Button size="sm" className="gap-1.5" onClick={() => abrir("nova")}>
          <Plus className="h-4 w-4" />
          Nova jornada
        </Button>
      }
    >
      {jornadas.length === 0 ? (
        <EmptyState
          icon={Zap}
          title="Nenhuma jornada ainda"
          description="Crie a primeira jornada para automatizar o primeiro contato."
          action={<Button onClick={() => abrir("nova")}>Criar jornada</Button>}
        />
      ) : (
        <div className="grid gap-6 p-6 lg:grid-cols-[340px_1fr]">
          {/* -------------------------------------------- lista de jornadas */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Jornadas
            </h3>

            {jornadas.map((jornada) => (
              <Card
                key={jornada.id}
                onClick={() => setSelecionadaId(jornada.id)}
                onKeyDown={(e) => e.key === "Enter" && setSelecionadaId(jornada.id)}
                tabIndex={0}
                role="button"
                aria-current={ativa?.id === jornada.id}
                className={cn(
                  "cursor-pointer p-4 transition-all hover:shadow-md",
                  ativa?.id === jornada.id && "ring-2 ring-primary",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{jornada.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{jornada.trigger}</p>
                  </div>

                  {/* O clique no switch não deve selecionar o card. */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={jornada.active}
                      onCheckedChange={(valor) =>
                        atualizar.mutate({ id: jornada.id, active: valor })
                      }
                      aria-label={`${jornada.active ? "Desativar" : "Ativar"} ${jornada.name}`}
                    />
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                  <Badge variant="secondary" className="text-[10px]">
                    {jornada.steps.length} {jornada.steps.length === 1 ? "etapa" : "etapas"}
                  </Badge>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatSchedule(jornada.schedule)}
                  </span>
                </div>
              </Card>
            ))}
          </div>

          {/* --------------------------------------------- jornada ativa */}
          {ativa && (
            <div className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-3">
                  <div className="min-w-0">
                    <CardTitle className="font-display text-lg">{ativa.name}</CardTitle>
                    <p className="mt-1 text-xs text-muted-foreground">{ativa.trigger}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge variant={ativa.active ? "success" : "outline"} className="text-[10px]">
                        {ativa.active ? "Ativa" : "Pausada"}
                      </Badge>
                      <Badge variant="secondary" className="gap-1 text-[10px]">
                        <Clock className="h-3 w-3" />
                        {formatSchedule(ativa.schedule)}
                      </Badge>
                      {ativa.triggerKeywords && (
                        <Badge variant="outline" className="text-[10px]">
                          {ativa.triggerKeywords}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => abrir("editar")}>
                      <Pencil className="h-3.5 w-3.5" />
                      Editar
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => abrir("janela")}>
                      <Settings2 className="h-3.5 w-3.5" />
                      Janela de envio
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-destructive hover:bg-destructive/10"
                      onClick={() => abrir("excluir")}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Excluir
                    </Button>
                  </div>
                </CardHeader>
              </Card>

              {/* ------------------------------------------------ etapas */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="font-display text-base">Etapas do fluxo</CardTitle>
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setEtapaEmEdicao({ aberta: true, etapa: null })}
                  >
                    <Plus className="h-4 w-4" />
                    Adicionar etapa
                  </Button>
                </CardHeader>

                <CardContent className="space-y-3">
                  {ativa.steps.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      Nenhuma etapa ainda. Adicione a primeira mensagem do fluxo.
                    </p>
                  ) : (
                    ativa.steps.map((etapa, indice) => {
                      const meta = stepTypeMeta(etapa.type);
                      return (
                        <div
                          key={etapa.id}
                          className="group flex gap-3 rounded-xl border border-border/60 bg-card p-4 transition-colors hover:border-primary/40"
                        >
                          <div className="flex flex-col items-center">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                              {indice + 1}
                            </div>
                            {indice < ativa.steps.length - 1 && (
                              <div className="mt-1 w-px flex-1 bg-border" />
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="secondary" className="gap-1 text-[10px]">
                                <meta.icon className="h-3 w-3" />
                                {meta.label}
                              </Badge>
                              <Badge variant="outline" className="gap-1 text-[10px]">
                                <Clock className="h-3 w-3" />
                                {etapa.delay}
                              </Badge>
                            </div>

                            <p className="mt-2 whitespace-pre-wrap text-sm">
                              {etapa.message || <span className="text-muted-foreground">Sem mensagem</span>}
                            </p>

                            {etapa.attachment?.name && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                Anexo: {etapa.attachment.name}
                              </p>
                            )}
                          </div>

                          <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setEtapaEmEdicao({ aberta: true, etapa })}
                              aria-label={`Editar etapa ${indice + 1}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              onClick={() =>
                                removerEtapa.mutate({ automationId: ativa.id, stepId: etapa.id })
                              }
                              aria-label={`Remover etapa ${indice + 1}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* --------------------------------------------------------- diálogos */}
      <NewJourneyDialog open={dialogos.nova} onClose={() => fechar("nova")} />
      <EditJourneyDialog automation={ativa} open={dialogos.editar} onClose={() => fechar("editar")} />
      <ScheduleDialog automation={ativa} open={dialogos.janela} onClose={() => fechar("janela")} />
      <DeleteJourneyDialog
        automation={ativa}
        open={dialogos.excluir}
        onClose={() => fechar("excluir")}
      />
      <StepEditorDialog
        automationId={ativa?.id}
        step={etapaEmEdicao.etapa}
        open={etapaEmEdicao.aberta}
        onClose={() => setEtapaEmEdicao({ aberta: false, etapa: null })}
      />
    </AppShell>
  );
}
